import { DocumentNode, gql, useMutation } from '@apollo/client';
import { MenuItem, useDisclosure, useToast } from '@chakra-ui/react';
import * as React from 'react';

import {
  EModItemResolutionState,
  EModerationReason,
} from '@/client/legacy-shared-modules/moderation-item/moderationType';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSignUpPrompt } from '@/hooks/useSignUpPrompt';
import { logError } from '@/imports/logging/ClientLogger';
import { FlagIcon } from '@/imports/ui/chakra/feather';

import { ReportCommentDropdownItem_CommentFragment } from './graphql-types/ReportCommentDropdownItem_CommentFragment';
import {
  ReportCommentDropdownItem_CreateModerationItem,
  ReportCommentDropdownItem_CreateModerationItemVariables,
} from './graphql-types/ReportCommentDropdownItem_CreateModerationItem';

const COMMENT_FRAGMENT = gql`
  fragment ReportCommentDropdownItem_CommentFragment on Comment {
    _id
    moderationItem {
      _id
      resolutionState
    }
  }
`;

const CREATE_MODERATION_ITEM = gql`
  mutation ReportCommentDropdownItem_CreateModerationItem(
    $input: CreateModerationItemInputV2!
  ) {
    createModerationItemV2(input: $input) {
      data {
        _id
        comment {
          _id
          ...ReportCommentDropdownItem_CommentFragment
        }
      }
    }
  }
  ${COMMENT_FRAGMENT}
`;

export const ReportCommentDropdownItem: React.FC<{
  comment: ReportCommentDropdownItem_CommentFragment;
}> & {
  commentFragment: DocumentNode;
} = ({ comment }) => {
  const confirmDialog = useDisclosure();
  const { promptModal, withPrompt } = useSignUpPrompt();

  const [createModerationItem] = useMutation<
    ReportCommentDropdownItem_CreateModerationItem,
    ReportCommentDropdownItem_CreateModerationItemVariables
  >(CREATE_MODERATION_ITEM);

  const toast = useToast();

  const reportCommentAsync = async (): Promise<void> => {
    try {
      await createModerationItem({
        variables: {
          input: {
            data: {
              resolutionState: EModItemResolutionState.PENDING,
              reason: EModerationReason.Reported,
              commentId: comment._id,
            },
          },
        },
      });
      toast({
        title: `This comment has been reported.`,
        status: 'success',
      });
    } catch (err) {
      toast({
        title: `Failed to report comment.`,
        status: 'error',
      });
      logError(err);
    }
  };

  return (
    <>
      <MenuItem icon={<FlagIcon />} onClick={withPrompt(confirmDialog.onOpen)}>
        Report this Comment
      </MenuItem>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title="Report this Comment"
        body="Reporting this comment will hide it from other community members until it is reviewed by a community admin. Are you sure you want to report this post?"
        confirmButtonText="Report comment"
        onConfirm={reportCommentAsync}
      />
      {promptModal}
    </>
  );
};

ReportCommentDropdownItem.commentFragment = COMMENT_FRAGMENT;
